/**
 * Enhancement (Jun-2026) — Provisions-Historie (12 Monate) + anonyme Team-
 * Bestenliste im Verkäufer-Dashboard.
 */
import { useState, useEffect } from "react";
import { BarChart3, Trophy, Loader2 } from "lucide-react";
import api, { formatPrice } from "../../lib/api";

export default function SellerCommissionStatsCard() {
    const [hist, setHist] = useState(null);
    const [board, setBoard] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/me/seller-commissions/history").catch(() => ({ data: null })),
            api.get("/me/seller-commissions/leaderboard").catch(() => ({ data: null })),
        ]).then(([h, b]) => {
            if (!alive) return;
            setHist(h.data);
            setBoard(b.data);
            setLoading(false);
        });
        return () => { alive = false; };
    }, []);

    if (loading) {
        return <div className="py-6 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Lädt …</div>;
    }
    const months = hist?.months || [];
    const maxVal = Math.max(1, ...months.map((m) => m.earned || 0));
    const entries = board?.entries || [];

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="seller-commission-stats">
            {/* Monats-Verlauf */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-sm p-4" data-testid="commission-history-chart">
                <h3 className="font-display font-bold text-sm text-[#111827] mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-indigo-600" /> Provision der letzten 12 Monate
                </h3>
                {months.every((m) => (m.earned || 0) === 0) ? (
                    <div className="text-sm text-gray-400 py-6 text-center">Noch keine Provisionsdaten.</div>
                ) : (
                    <div className="flex items-end gap-1.5 h-36" data-testid="commission-history-bars">
                        {months.map((m) => {
                            const h = Math.round(((m.earned || 0) / maxVal) * 100);
                            const ch = (m.earned || 0) > 0 ? Math.round(((m.confirmed || 0) / (m.earned || 1)) * h) : 0;
                            return (
                                <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full group" title={`${m.label}: ${formatPrice(m.earned)} (bestätigt ${formatPrice(m.confirmed)})`} data-testid={`commission-history-bar-${m.month}`}>
                                    <div className="w-full bg-indigo-100 rounded-t-sm relative flex flex-col justify-end" style={{ height: `${Math.max(h, 2)}%` }}>
                                        <div className="w-full bg-indigo-600 rounded-t-sm" style={{ height: `${Math.max(ch, 0)}%` }} />
                                    </div>
                                    <div className="text-[8px] text-gray-400 mt-1 rotate-0 whitespace-nowrap">{m.label}</div>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-500">
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-indigo-600 rounded-sm inline-block" /> bestätigt</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-indigo-100 rounded-sm inline-block" /> erwirtschaftet</span>
                </div>
            </div>

            {/* Bestenliste */}
            <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="commission-leaderboard">
                <h3 className="font-display font-bold text-sm text-[#111827] mb-1 flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-amber-500" /> Bestenliste {board?.year || ""}
                </h3>
                {board?.my_rank ? (
                    <p className="text-[11px] text-gray-500 mb-2" data-testid="leaderboard-my-rank">Dein Platz: <strong className="text-indigo-700">{board.my_rank} von {board.total}</strong></p>
                ) : (
                    <p className="text-[11px] text-gray-400 mb-2">Noch keine Platzierung.</p>
                )}
                <div className="space-y-1">
                    {entries.slice(0, 8).map((e) => (
                        <div key={e.rank} className={`flex items-center justify-between px-2 py-1 rounded-sm text-xs ${e.is_me ? "bg-indigo-50 border border-indigo-200 font-bold" : "bg-gray-50"}`} data-testid={`leaderboard-entry-${e.rank}`}>
                            <span className="flex items-center gap-2">
                                <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] font-black ${e.rank === 1 ? "bg-amber-400 text-[#111827]" : e.rank === 2 ? "bg-gray-300 text-[#111827]" : e.rank === 3 ? "bg-orange-300 text-[#111827]" : "bg-gray-200 text-gray-600"}`}>{e.rank}</span>
                                {e.label}
                            </span>
                            <span className="font-mono-num">{formatPrice(e.confirmed)}</span>
                        </div>
                    ))}
                    {entries.length === 0 && <div className="text-xs text-gray-400 py-3 text-center">Keine Daten.</div>}
                </div>
                {board?.my_breakdown && (board.my_breakdown.accessory > 0 || board.my_breakdown.service > 0 || board.my_breakdown.vehicle > 0 || board.my_breakdown.extra > 0) && (
                    <div className="mt-3 pt-3 border-t border-gray-100" data-testid="leaderboard-my-breakdown">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Deine bestätigte Provision nach Quelle</div>
                        <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                            {[
                                { k: "vehicle", label: "Fahrzeug", color: "text-indigo-700" },
                                { k: "extra", label: "Zusatzertrag", color: "text-violet-700" },
                                { k: "accessory", label: "Zubehör", color: "text-emerald-700" },
                                { k: "service", label: "Dienstleistung", color: "text-amber-700" },
                            ].map((c) => (
                                <div key={c.k} className="flex items-center justify-between bg-gray-50 rounded-sm px-2 py-1" data-testid={`leaderboard-breakdown-${c.k}`}>
                                    <span className="text-gray-600">{c.label}</span>
                                    <span className={`font-mono-num font-bold ${c.color}`}>{formatPrice(board.my_breakdown[c.k] || 0)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <p className="text-[9px] text-gray-400 mt-2">Anonymisiert — andere Verkäufer ohne Klarnamen.</p>
            </div>
        </div>
    );
}
